
import React, { useRef } from "react";
import { Popup } from "../common/Popup";
import { Button } from "./button";
import { useApiFetch } from "@/helper/apiFetch";
import { string } from "zod";
import { blobToDataURL } from "@/helper/sort";
import { Template } from "../spot/SpotSchema";
import { format } from "path";

export type EditableSvgHandle = {
  /** Returns the current <svg> as a string */
  getSvgString: () => string | null;
};

type EditableSvgProps = {
  template: Template ;
  pageNumber?: number;
  /** not needed anymore */
  onChange?: () => void;

  className?: string;
  style?: React.CSSProperties;
};

export const EditableSvg = React.forwardRef<EditableSvgHandle, EditableSvgProps>(
  ({ template, pageNumber = 0, onChange, className, style }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [svgMarkup, setSvgMarkup] = React.useState<string | null>(null);
    const [editElement, setEditElement] = React.useState<SVGElement | null>(null);
    const [editImgElement, setEditImgElement] = React.useState<SVGElement | null>(null);
    const [svgElement, setSVGElement] = React.useState<SVGElement | null>(null);
    const apiFetch = useApiFetch();

    const fileInputRef = useRef<HTMLInputElement>(null);
    
     
    
      const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
    
        e.target.value = "";  
        const reader = new FileReader();
        reader.onloadend = () => {
         
          const base64 = (reader.result as string).split(",")[1]; // strip prefix
          
          if(editImgElement?.hasAttribute("href"))
            editImgElement?.setAttribute("href", "data:image/png;base64," + base64);
            else
            editImgElement?.setAttribute("xlink:href", "data:image/png;base64," + base64);

     
           template.templateFormats[0].html = containerRef.current?.innerHTML || "";
           template.templateFormats[0].isGenerated = false;
              onChange?.();
    
        };
        reader.readAsDataURL(file);
      };
      
    const handler = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      setEditElement(e.currentTarget as SVGElement);



    };
const imgHandler = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      setEditImgElement(e.currentTarget as SVGElement);

fileInputRef.current?.click();

    };


    // Expose getSvgString() to parent
    React.useImperativeHandle(ref, () => ({
      getSvgString() {
        const svg = containerRef.current?.querySelector("svg");
        return svg ? svg.outerHTML : null;
      },
    }), []);

    // Load SVG text
    React.useEffect(() => {
      let cancelled = false;
      (async () => {

        const res = await apiFetch(`/App/ServeTemplate?path=${template.templateFormats[0].pathServer}`, { method: "GET", cache: "no-store" });
        const txt = await res.text();
        if (!cancelled) setSvgMarkup(txt);
      })().catch(console.error);
      return () => { cancelled = true; };
    },[]);

    // After injecting SVG, attach handlers to .editable
    React.useEffect(() => {
      const root = containerRef.current;
  if (!root || svgMarkup == null) return;

  
      // Inject markup
        root.innerHTML = svgMarkup;

      const svg = root.querySelector("svg");
      if (!svg) return;
      setSVGElement(svg);
       template.templateFormats[0].html = svgMarkup;
        template.templateFormats[0].isGenerated = false;
      onChange?.();
      const imgs = Array.from(svg.querySelectorAll<SVGImageElement>("image"));


      imgs.forEach(n => {
        let src = n.getAttribute("xlink:href");
        if (!src?.startsWith("data:image")) {
          apiFetch(`/App/ServeTemplate?path=${template.templateFormats[0].pathServer}` + (src ? `&image=${encodeURIComponent(src)}` : ""))
            .then(res => {
              if (!res.ok) throw new Error("Failed to fetch image");
              return res.blob();
            })
            .then(blob => blobToDataURL(blob))
            .then(url => {
           
              n.setAttribute("xlink:href", url as string);

            template.templateFormats[0].html = containerRef.current?.innerHTML || "";
            template.templateFormats[0].isGenerated = false;

              onChange?.();

            })
            .catch(err => {
              console.error(err);
            });
          // n.setAttribute("xlink:href", `${API_BASE_URL}/App/ServeTemplate/${templateId}` + (src ? `?image=${encodeURIComponent(src)}` : ""));
        }
        if(n.classList.contains("editable"))
        {
           n.addEventListener("click", imgHandler);
         
           const r = n.getBoundingClientRect();
            let ov = document.createElement('div');
            ov.className = 'edit-overlay';
            document.body.appendChild(ov);
            //img.addEventListener('mouseenter', () => img.classList.add('img-hovering'));
            //img.addEventListener('mouseleave', () => img.classList.remove('img-hovering'));
        
          ov.style.left = r.left + 'px';
          ov.style.top = r.top + 'px';
          ov.style.width = r.width + 'px';
          ov.style.height = r.height + 'px';
        }
      });


      const editableNodes = Array.from(svg.querySelectorAll<SVGElement>("text.editable, tspan.editable"));

      editableNodes.forEach(n => n.addEventListener("click", handler));
      return () => editableNodes.forEach(n => n.removeEventListener("click", handler));
    }, [svgMarkup]);


    React.useEffect(() => {
      if (!svgElement) return;
      let height = parseFloat(svgElement.getAttribute("sheight") ?? "");
      svgElement.style.transform = `translateY(-${height * pageNumber}px)`;
    }, [pageNumber, svgElement]);


    return (
      <div style={{ overflow: "hidden", height: `${parseFloat(svgElement?.getAttribute("sheight") ?? svgElement?.getAttribute("height") ?? "") * 0.8}px`, width: `${parseFloat(svgElement?.getAttribute("width") ?? "") * 0.8}px` }}>
        <div
          ref={containerRef}
          className={className}
          style={{ width: "100%", height: "auto", ...style }}
          // make text elements clickable even if nested
          onMouseOver={(e) => {
            const el = (e.target as Element);
            if (el instanceof SVGElement && el.classList.contains("editable")) {
              (el as any).style.cursor = "text";
            }
          }}
        />

<input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
        <Popup
          isOpen={editElement !== null}
          onClose={() => setEditElement(null)}
          title="Text"
           className="w-[80%]"
        >

          <input className="w-[100%] rounded-md border border-input p-2" id="editElement" defaultValue={editElement?.textContent ?? ""}></input>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={async () => {
              if (editElement)
              {
                editElement.textContent = document.getElementById("editElement")?.value;
              template.templateFormats[0].isGenerated = false;
              template.templateFormats[0].html = containerRef.current?.innerHTML || "";

                onChange?.();
              }
              setEditElement(null);
            }}>OK</Button>
            <Button onClick={async () => {
              setEditElement(null);
            }}>Cancel</Button>

          </div>
        </Popup>
      </div>
    );
  }
);
